/**
 * Bridge from the "New Connection from Clipboard" command-palette action to the
 * New Connection dialog: the command parses the clipboard and stashes the
 * result here, then opens the dialog. NewConnectionPopup (new mode) consumes the
 * pending prefill on mount, applies it to the form, and shows the confirmation
 * line — mirroring a dialog paste.
 *
 * Mirrors the imperative `pending`/`set`/`consume` pattern of
 * `src/core/licensing/store/trialPromptStore.ts`. The parse result carries only
 * mapped field values; the raw clipboard string is never stored here.
 */
import { create } from 'zustand';
import type { ConnectionStringParseResult } from '../services/connection-string/parseConnectionString';

/** A pending prefill: the parse result plus the (masked-on-display) source text. */
export type ConnectionStringPrefill = {
  result: ConnectionStringParseResult;
  /** The raw string the user had on their clipboard, shown back in the field. */
  input: string;
};

interface ConnectionStringPrefillStore {
  pending: (ConnectionStringPrefill & { handoffId: string }) | null;
  begin: (prefill: ConnectionStringPrefill) => string;
  consume: (handoffId: string) => ConnectionStringPrefill | null;
  cancel: (handoffId: string) => boolean;
}

export const useConnectionStringPrefillStore = create<ConnectionStringPrefillStore>((set, get) => ({
  pending: null,
  begin: (prefill) => {
    const handoffId = crypto.randomUUID();
    set({ pending: { ...prefill, handoffId } });
    return handoffId;
  },
  consume: (handoffId) => {
    const pending = get().pending;
    if (!pending || pending.handoffId !== handoffId) return null;
    set({ pending: null });
    const { handoffId: _id, ...prefill } = pending;
    return prefill;
  },
  cancel: (handoffId) => {
    if (get().pending?.handoffId !== handoffId) return false;
    set({ pending: null });
    return true;
  },
}));
